import { orderApi } from '@/api/orders';
import { useCurrency } from '@/hooks/useCurrency';
import {
    ArrowLeftIcon,
    BuildingStorefrontIcon,
    CheckCircleIcon,
    ClockIcon,
    CubeIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    PencilSquareIcon,
    TrashIcon,
    TruckIcon,
    UserIcon,
    XCircleIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// ── Shared layout ─────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--gray-50);
`;

const PageContent = styled.div`
  padding: 2.5rem 0;
`;

const PageHeader = styled.header`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Main = styled.main`
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 2rem;
`;

// ── List view ─────────────────────────────────────────────────────────────────

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  position: relative;
  width: 300px;

  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 1.25rem;
    height: 1.25rem;
    color: var(--gray-400);
  }

  input {
    width: 100%;
    padding: 0.5rem 1rem 0.5rem 2.5rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.5rem;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
    }
  }
`;

const StatusFilter = styled.select`
  padding: 0.5rem 2.5rem 0.5rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);

  h3 {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--gray-600);
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.875rem;
    font-weight: 700;
    color: var(--gray-900);
  }
`;

const OrdersList = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const OrdersTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: var(--gray-50);
    border-bottom: 1px solid var(--gray-200);

    th {
      padding: 0.75rem 1rem;
      text-align: left;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--gray-600);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid var(--gray-200);
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: var(--gray-50);
      }
      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 1rem;
      font-size: 0.875rem;
      color: var(--gray-700);
    }
  }
`;

const OrderId = styled.div`
  font-weight: 600;
  color: var(--primary-600);
`;

const CustomerName = styled.div`
  font-weight: 500;
  color: var(--gray-900);
`;

const CustomerEmail = styled.div`
  font-size: 0.75rem;
  color: var(--gray-500);
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;

  svg {
    width: 1rem;
    height: 1rem;
  }

  ${({ $status }) => {
    switch ($status) {
      case 'pending':
        return 'background:#fef3c7;color:#92400e;';
      case 'processing':
        return 'background:#dbeafe;color:#1e40af;';
      case 'shipped':
        return 'background:#e0e7ff;color:#3730a3;';
      case 'delivered':
        return 'background:#d1fae5;color:#065f46;';
      case 'cancelled':
        return 'background:#fee2e2;color:#991b1b;';
      default:
        return 'background:var(--gray-100);color:var(--gray-700);';
    }
  }}
`;

const Amount = styled.div`
  font-weight: 600;
  color: var(--gray-900);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--gray-900);
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--gray-500);
    font-size: 0.875rem;
  }
`;

// ── Detail / edit view ────────────────────────────────────────────────────────

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-600);
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0;
  margin-bottom: 0.5rem;

  svg {
    width: 1rem;
    height: 1rem;
  }
  &:hover {
    color: var(--gray-900);
  }
`;

const DetailTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const DetailSubtitle = styled.p`
  font-size: 0.875rem;
  color: var(--gray-500);
  margin-top: 0.15rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--gray-500);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  svg {
    width: 0.9rem;
    height: 0.9rem;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  label {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--gray-400);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const ReadValue = styled.span`
  font-size: 0.875rem;
  color: var(--gray-800);
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  background: white;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.08);
  }
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.08);
  }
`;

const Textarea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  background: white;
  resize: vertical;
  min-height: 72px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.08);
  }
`;

const FullWidth = styled(Field)`
  grid-column: 1 / -1;
`;

const ItemsCard = styled(Card)`
  grid-column: 1 / -1;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  thead th {
    text-align: left;
    padding: 0.5rem 0.75rem;
    background: var(--gray-50);
    color: var(--gray-500);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid var(--gray-200);
  }

  tbody td {
    padding: 0.75rem;
    color: var(--gray-700);
    border-bottom: 1px solid var(--gray-100);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
  .right {
    text-align: right;
  }
  .mono {
    font-family: monospace;
    color: var(--gray-400);
  }
  .bold {
    font-weight: 600;
    color: var(--gray-900);
  }
  .actions {
    text-align: right;
    white-space: nowrap;
    width: 1%;
  }
`;

const ItemActionBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: none;
  border-radius: 0.25rem;
  background: none;
  cursor: pointer;
  color: ${({ $danger }) =>
    $danger ? 'var(--red-500, #ef4444)' : 'var(--gray-400)'};
  transition:
    color 0.15s,
    background 0.15s;

  &:hover {
    background: ${({ $danger }) => ($danger ? '#fee2e2' : 'var(--gray-100)')};
    color: ${({ $danger }) => ($danger ? '#b91c1c' : 'var(--gray-700)')};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const ItemInput = styled.input`
  width: 100%;
  min-width: 60px;
  padding: 0.25rem 0.4rem;
  border: 1px solid var(--primary-600);
  border-radius: 0.25rem;
  font-size: 0.8rem;
  color: var(--gray-900);
  background: white;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(234, 88, 12, 0.15);
  }
`;

const SummaryCard = styled(Card)`
  grid-column: 1 / -1;
`;

const SummaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 320px;
  margin-left: auto;
`;

const SummaryRow = styled.div<{ $total?: boolean }>`
  display: flex;
  justify-content: space-between;
  font-size: ${({ $total }) => ($total ? '1rem' : '0.875rem')};
  font-weight: ${({ $total }) => ($total ? '700' : '400')};
  color: ${({ $total }) => ($total ? 'var(--gray-900)' : 'var(--gray-600)')};
  padding-top: ${({ $total }) => ($total ? '0.5rem' : '0')};
  border-top: ${({ $total }) =>
    $total ? '2px solid var(--gray-200)' : 'none'};
  margin-top: ${({ $total }) => ($total ? '0.25rem' : '0')};
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const BtnSecondary = styled.button`
  padding: 0.5rem 1.25rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  background: white;
  color: var(--gray-700);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: var(--gray-50);
  }
`;

const BtnPrimary = styled.button`
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 0.5rem;
  background: var(--primary-600);
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: var(--primary-700);
  }
`;

// ── Types ─────────────────────────────────────────────────────────────────────

interface OrderItem {
  sku: string;
  name: string;
  qty: number;
  unitPrice: number;
}

interface OrderAddress {
  fullName: string;
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
}

interface Order {
  id: string;
  customer: { name: string; email: string; phone?: string };
  total: number;
  subtotal?: number;
  shippingCost?: number;
  tax?: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  date: string;
  items: number;
  marketplace_id?: string;
  marketplace?: string;
  orderItems?: OrderItem[];
  shippingAddress?: OrderAddress;
  notes?: string;
  trackingNumber?: string;
}

// ── Mock data ─────────────────────────────────────────────────────────────────


// ── Helpers ───────────────────────────────────────────────────────────────────

function getStatusIcon(status: string) {
  switch (status) {
    case 'pending':
      return <ClockIcon />;
    case 'processing':
      return <ClockIcon />;
    case 'shipped':
      return <TruckIcon />;
    case 'delivered':
      return <CheckCircleIcon />;
    case 'cancelled':
      return <XCircleIcon />;
    default:
      return null;
  }
}

// ── Detail / edit form ────────────────────────────────────────────────────────

interface DetailFormProps {
  order: Order;
  onBack: () => void;
}

const DetailForm: FC<DetailFormProps> = ({ order, onBack }) => {
  const { symbol: currencySymbol } = useCurrency();
  const [status, setStatus] = useState(order.status);
  const [trackingNumber, setTrackingNumber] = useState(
    order.trackingNumber ?? ''
  );
  const [marketplace, setMarketplace] = useState(order.marketplace ?? '');
  const [marketplaceId, setMarketplaceId] = useState(
    order.marketplace_id ?? ''
  );
  const [notes, setNotes] = useState(order.notes ?? '');

  // Item editing
  const [editableItems, setEditableItems] = useState<OrderItem[]>(
    order.orderItems ?? []
  );
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<OrderItem | null>(null);

  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditDraft({ ...editableItems[idx] });
  };
  const cancelEdit = () => {
    setEditingIdx(null);
    setEditDraft(null);
  };
  const saveEdit = () => {
    if (editDraft === null || editingIdx === null) return;
    setEditableItems((prev) =>
      prev.map((it, i) => (i === editingIdx ? editDraft : it))
    );
    cancelEdit();
  };
  const deleteItem = (idx: number) => {
    setEditableItems((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) cancelEdit();
  };

  const addr = order.shippingAddress;
  const [shipLine1, setShipLine1] = useState(addr?.line1 ?? '');
  const [shipLine2, setShipLine2] = useState(addr?.line2 ?? '');
  const [shipCity, setShipCity] = useState(addr?.city ?? '');
  const [shipState, setShipState] = useState(addr?.state ?? '');
  const [shipPostal, setShipPostal] = useState(addr?.postalCode ?? '');
  const [shipCountry, setShipCountry] = useState(addr?.country ?? '');

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await orderApi.updateOrder(order.id, {
        status,
        tracking_number: trackingNumber,
        marketplace,
        marketplace_id: marketplaceId,
        notes,
        shipLine1,
        shipLine2,
        shipCity,
        shipState,
        shipPostal,
        shipCountry,
      });
      onBack();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer>
      <PageContent>
        <PageHeader>
          <BackBtn onClick={onBack}>
            <ArrowLeftIcon /> Back to Orders
          </BackBtn>
          <HeaderTop>
            <div>
              <DetailTitle>Order {order.id}</DetailTitle>
              <DetailSubtitle>
                {new Date(order.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
                {' · '}
                {order.customer.name}
              </DetailSubtitle>
            </div>
            <StatusBadge $status={status}>
              {getStatusIcon(status)}
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </StatusBadge>
          </HeaderTop>
        </PageHeader>

        <Main>
          <DetailGrid>
            {/* Customer (read-only) */}
            <Card>
              <CardTitle>
                <UserIcon /> Customer
              </CardTitle>
              <FieldGrid>
                <Field>
                  <label>Name</label>
                  <ReadValue>{order.customer.name}</ReadValue>
                </Field>
                <Field>
                  <label>Email</label>
                  <ReadValue>{order.customer.email}</ReadValue>
                </Field>
                {order.customer.phone && (
                  <Field>
                    <label>Phone</label>
                    <ReadValue>{order.customer.phone}</ReadValue>
                  </Field>
                )}
              </FieldGrid>
            </Card>

            {/* Order settings (editable) */}
            <Card>
              <CardTitle>
                <BuildingStorefrontIcon /> Order Details
              </CardTitle>
              <FieldGrid>
                <Field>
                  <label>Status</label>
                  <Select
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as Order['status'])
                    }
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </Select>
                </Field>
                <Field>
                  <label>Tracking #</label>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="e.g. 1Z999AA1…"
                  />
                </Field>
                <Field>
                  <label>Marketplace</label>
                  <Input
                    value={marketplace}
                    onChange={(e) => setMarketplace(e.target.value)}
                    placeholder="Amazon, eBay…"
                  />
                </Field>
                <Field>
                  <label>Marketplace ID</label>
                  <Input
                    value={marketplaceId}
                    onChange={(e) => setMarketplaceId(e.target.value)}
                    placeholder="e.g. amz-001"
                  />
                </Field>
              </FieldGrid>
            </Card>

            {/* Shipping address (editable) */}
            <Card>
              <CardTitle>
                <MapPinIcon /> Shipping Address
              </CardTitle>
              <FieldGrid>
                <FullWidth>
                  <label>Street</label>
                  <Input
                    value={shipLine1}
                    onChange={(e) => setShipLine1(e.target.value)}
                  />
                </FullWidth>
                <FullWidth>
                  <label>Apt / Suite / Floor</label>
                  <Input
                    value={shipLine2}
                    onChange={(e) => setShipLine2(e.target.value)}
                  />
                </FullWidth>
                <Field>
                  <label>City</label>
                  <Input
                    value={shipCity}
                    onChange={(e) => setShipCity(e.target.value)}
                  />
                </Field>
                <Field>
                  <label>State / Region</label>
                  <Input
                    value={shipState}
                    onChange={(e) => setShipState(e.target.value)}
                  />
                </Field>
                <Field>
                  <label>Postal Code</label>
                  <Input
                    value={shipPostal}
                    onChange={(e) => setShipPostal(e.target.value)}
                  />
                </Field>
                <Field>
                  <label>Country</label>
                  <Input
                    value={shipCountry}
                    onChange={(e) => setShipCountry(e.target.value)}
                  />
                </Field>
              </FieldGrid>
            </Card>

            {/* Notes (editable) */}
            <Card>
              <CardTitle>Notes</CardTitle>
              <FieldGrid>
                <FullWidth>
                  <label>Order notes</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Internal notes or customer instructions…"
                  />
                </FullWidth>
              </FieldGrid>
            </Card>

            {/* Items */}
            <ItemsCard>
              <CardTitle>
                <CubeIcon /> Items ({editableItems.length})
              </CardTitle>
              <ItemsTable>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th className="right">Qty</th>
                    <th className="right">Unit price</th>
                    <th className="right">Line total</th>
                    <th className="actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {editableItems.map((item, idx) =>
                    editingIdx === idx && editDraft ? (
                      <tr key={item.sku}>
                        <td>
                          <ItemInput
                            value={editDraft.sku}
                            onChange={(e) =>
                              setEditDraft({
                                ...editDraft,
                                sku: e.target.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <ItemInput
                            value={editDraft.name}
                            onChange={(e) =>
                              setEditDraft({
                                ...editDraft,
                                name: e.target.value,
                              })
                            }
                            style={{ minWidth: 140 }}
                          />
                        </td>
                        <td className="right">
                          <ItemInput
                            type="number"
                            min={1}
                            value={editDraft.qty}
                            onChange={(e) =>
                              setEditDraft({
                                ...editDraft,
                                qty: Number(e.target.value),
                              })
                            }
                            style={{ width: 56 }}
                          />
                        </td>
                        <td className="right">
                          <ItemInput
                            type="number"
                            min={0}
                            step={0.01}
                            value={editDraft.unitPrice}
                            onChange={(e) =>
                              setEditDraft({
                                ...editDraft,
                                unitPrice: Number(e.target.value),
                              })
                            }
                            style={{ width: 80 }}
                          />
                        </td>
                        <td className="right bold">
                          {currencySymbol}{(editDraft.qty * editDraft.unitPrice).toFixed(2)}
                        </td>
                        <td className="actions">
                          <ItemActionBtn title="Save" onClick={saveEdit}>
                            <CheckCircleIcon />
                          </ItemActionBtn>
                          <ItemActionBtn title="Cancel" onClick={cancelEdit}>
                            <XMarkIcon />
                          </ItemActionBtn>
                        </td>
                      </tr>
                    ) : (
                      <tr key={item.sku}>
                        <td className="mono">{item.sku}</td>
                        <td className="bold">{item.name}</td>
                        <td className="right">{item.qty}</td>
                        <td className="right">{currencySymbol}{item.unitPrice.toFixed(2)}</td>
                        <td className="right bold">
                          {currencySymbol}{(item.qty * item.unitPrice).toFixed(2)}
                        </td>
                        <td className="actions">
                          <ItemActionBtn
                            title="Edit"
                            onClick={() => startEdit(idx)}
                          >
                            <PencilSquareIcon />
                          </ItemActionBtn>
                          <ItemActionBtn
                            $danger
                            title="Delete"
                            onClick={() => deleteItem(idx)}
                          >
                            <TrashIcon />
                          </ItemActionBtn>
                        </td>
                      </tr>
                    )
                  )}
                  {editableItems.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          textAlign: 'center',
                          color: 'var(--gray-400)',
                          padding: '1.5rem',
                        }}
                      >
                        No items
                      </td>
                    </tr>
                  )}
                </tbody>
              </ItemsTable>
            </ItemsCard>

            {/* Summary (read-only) */}
            <SummaryCard>
              <CardTitle>Order Summary</CardTitle>
              <SummaryRows>
                {order.subtotal != null && (
                  <SummaryRow>
                    <span>Subtotal</span>
                    <span>{currencySymbol}{order.subtotal.toFixed(2)}</span>
                  </SummaryRow>
                )}
                {order.shippingCost != null && (
                  <SummaryRow>
                    <span>Shipping</span>
                    <span>{currencySymbol}{order.shippingCost.toFixed(2)}</span>
                  </SummaryRow>
                )}
                {order.tax != null && (
                  <SummaryRow>
                    <span>Tax</span>
                    <span>{currencySymbol}{order.tax.toFixed(2)}</span>
                  </SummaryRow>
                )}
                <SummaryRow $total>
                  <span>Total</span>
                  <span>{currencySymbol}{order.total.toFixed(2)}</span>
                </SummaryRow>
              </SummaryRows>
            </SummaryCard>
          </DetailGrid>

          <FormActions>
            <BtnSecondary onClick={onBack} disabled={isSaving}>Cancel</BtnSecondary>
            <BtnPrimary onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save Changes'}
            </BtnPrimary>
          </FormActions>
        </Main>
      </PageContent>
    </PageContainer>
  );
};

// ── List view component ───────────────────────────────────────────────────────

const Orders: FC = () => {
  const { symbol: currencySymbol } = useCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOrders = (search: string, status: string) => {
    setLoading(true);
    setError(null);
    orderApi
      .getOrders({
        search: search || undefined,
        status: status !== 'all' ? status : undefined,
        pageSize: 100,
      })
      .then((res) => {
        // API returns { items, total, ... } in data field
        const data = res as unknown as { items: Order[]; total: number };
        setOrders(data.items ?? []);
        setTotal(data.total ?? 0);
      })
      .catch(() => setError('Failed to load orders'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders('', 'all');
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOrders(value, statusFilter), 400);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    fetchOrders(searchTerm, value);
  };

  const handleOrderClick = (order: Order) => {
    // Fetch full detail (with orderItems)
    orderApi
      .getOrder(order.id)
      .then((full) => setSelectedOrder(full as unknown as Order))
      .catch(() => setSelectedOrder(order));
  };

  if (selectedOrder) {
    return (
      <DetailForm
        order={selectedOrder}
        onBack={() => {
          setSelectedOrder(null);
          fetchOrders(searchTerm, statusFilter);
        }}
      />
    );
  }

  const stats = {
    total,
    pending: orders.filter((o) => o.status === 'pending').length,
    processing: orders.filter((o) => o.status === 'processing').length,
    shipped: orders.filter((o) => o.status === 'shipped').length,
    delivered: orders.filter((o) => o.status === 'delivered').length,
  };

  return (
    <PageContainer>
      <PageContent>
        <PageHeader>
          <HeaderTop>
            <Title>Orders</Title>
            <Filters>
              <SearchBox>
                <MagnifyingGlassIcon />
                <input
                  type="text"
                  placeholder="Search orders…"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </SearchBox>
              <StatusFilter
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </StatusFilter>
            </Filters>
          </HeaderTop>
        </PageHeader>

        <Main>
          <StatsGrid>
            <StatCard>
              <h3>Total Orders</h3>
              <p>{stats.total}</p>
            </StatCard>
            <StatCard>
              <h3>Pending</h3>
              <p>{stats.pending}</p>
            </StatCard>
            <StatCard>
              <h3>Processing</h3>
              <p>{stats.processing}</p>
            </StatCard>
            <StatCard>
              <h3>Shipped</h3>
              <p>{stats.shipped}</p>
            </StatCard>
            <StatCard>
              <h3>Delivered</h3>
              <p>{stats.delivered}</p>
            </StatCard>
          </StatsGrid>

          <OrdersList>
            {loading ? (
              <EmptyState>
                <p>Loading orders…</p>
              </EmptyState>
            ) : error ? (
              <EmptyState>
                <h3>Error loading orders</h3>
                <p>{error}</p>
              </EmptyState>
            ) : orders.length > 0 ? (
              <OrdersTable>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Marketplace</th>
                    <th>Marketplace ID</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} onClick={() => handleOrderClick(order)}>
                      <td>
                        <OrderId>{order.id}</OrderId>
                      </td>
                      <td>
                        <CustomerName>{order.customer.name}</CustomerName>
                        <CustomerEmail>{order.customer.email}</CustomerEmail>
                      </td>
                      <td>{order.marketplace || '—'}</td>
                      <td>{order.marketplace_id || '—'}</td>
                      <td>{order.items}</td>
                      <td>
                        <Amount>{currencySymbol}{order.total.toFixed(2)}</Amount>
                      </td>
                      <td>
                        <StatusBadge $status={order.status}>
                          {getStatusIcon(order.status)}
                          {order.status.charAt(0).toUpperCase() +
                            order.status.slice(1)}
                        </StatusBadge>
                      </td>
                      <td>{new Date(order.date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </OrdersTable>
            ) : (
              <EmptyState>
                <h3>No orders found</h3>
                <p>
                  {searchTerm || statusFilter !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Orders placed via your connected marketplaces will appear here'}
                </p>
              </EmptyState>
            )}
          </OrdersList>
        </Main>
      </PageContent>
    </PageContainer>
  );
};

export default Orders;
